import { GenerationStatus, MediaType } from '@repo/rpc-types-creativefabrica-modality-v1'
import type { OutputMedia } from '@repo/rpc-types-creativefabrica-modality-v1'
import type { GetConversationResponse } from '../gen/service/creativefabrica/studioagent/v1/service_pb'
import type {
  ConversationGeneration,
  OutputRef,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type { AidaMediaBase, AidaMediaKind, AidaMediaStatus } from '../types'
import { timestampToMs } from './normalize'
import { textOptions } from './plan'
import { OUTPUT_TOKEN } from './tokens'

const mediaKinds: Partial<Record<MediaType, AidaMediaKind>> = {
  [MediaType.VIDEO]: 'video',
  [MediaType.AUDIO]: 'audio',
}

export interface AidaConversationMedia extends AidaMediaBase {
  id: string
  generationId: string
  messageId?: string
  model: string
  createdAt: number
  duration?: number
  aspectRatio?: string
  options?: { name: string; value: string }[]
}

export function conversationMediaFrom(response: GetConversationResponse): AidaConversationMedia[] {
  const outputs = new Map<string, OutputRef>()
  for (const output of response.outputs) {
    if (!outputs.has(output.generationId)) {
      outputs.set(output.generationId, output)
    }
  }
  return response.generations
    .flatMap(entry => {
      const item = mediaItem(entry, outputs)
      return item ? [item] : []
    })
    .sort((a, b) => b.createdAt - a.createdAt)
}

function mediaItem(
  entry: ConversationGeneration,
  outputs: ReadonlyMap<string, OutputRef>,
): AidaConversationMedia | null {
  const generation = entry.generation
  if (!generation) {
    return null
  }
  const agentGenerationId = generation.metadata?.agent_generation_id || undefined
  const generationId = generation.generationId
  const id = agentGenerationId || generationId
  if (!id) {
    return null
  }
  const status = mediaStatus(generation.status)
  const matched = agentGenerationId ? outputs.get(agentGenerationId) : undefined
  const media = matched?.media ?? generation.outputMedia[0]
  const promptRef = matched ? promptRefFrom(matched.ref) : undefined
  return {
    id,
    generationId,
    ...(agentGenerationId ? { agentGenerationId } : {}),
    ...(media?.id ? { outputMediaId: media.id } : {}),
    ...(entry.messageId ? { messageId: entry.messageId } : {}),
    kind: mediaKinds[media?.mediaType ?? MediaType.IMAGE] ?? 'image',
    ...(status === 'ready' && media ? urlsFrom(media) : {}),
    model: generation.modelUsed,
    ...generationFacts(generation, media),
    status,
    ...(promptRef ? { promptRef } : {}),
    createdAt: timestampToMs(generation.createdAt),
    ...(generation.error?.message ? { errorMessage: generation.error.message } : {}),
  }
}

function generationFacts(
  generation: NonNullable<ConversationGeneration['generation']>,
  media: OutputMedia | undefined,
): Pick<AidaConversationMedia, 'duration' | 'aspectRatio' | 'options'> {
  const options = textOptions(generation.options)
  const duration = playbackSeconds(media)
  const aspectRatio = videoAspect(media)
  return {
    ...(duration ? { duration } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
    ...(options.length > 0 ? { options } : {}),
  }
}

function videoAspect(media: OutputMedia | undefined) {
  if (media?.content.case !== 'videoContent') {
    return undefined
  }
  const aspect = media.content.value.aspectRatio.trim()
  return aspect || undefined
}

function playbackSeconds(media: OutputMedia | undefined) {
  if (media?.content.case !== 'videoContent' && media?.content.case !== 'audioContent') {
    return undefined
  }
  const seconds = media.content.value.durationSeconds
  if (seconds > 0) {
    return seconds
  }
  return undefined
}

function mediaStatus(status: GenerationStatus): AidaMediaStatus {
  if (status === GenerationStatus.COMPLETED) {
    return 'ready'
  }
  if (status === GenerationStatus.FAILED || status === GenerationStatus.CANCELLED) {
    return 'failed'
  }
  return 'generating'
}

function urlsFrom(
  media: OutputMedia,
): Pick<AidaConversationMedia, 'url' | 'thumbnailUrl' | 'posterUrl'> {
  if (media.content.case === 'imageContent') {
    const image = media.content.value
    return {
      url: image.originalImageUrl || image.url,
      thumbnailUrl: widest(image.thumbnailUrls) || image.previewUrl || image.url,
    }
  }
  if (media.content.case === 'videoContent') {
    const video = media.content.value
    const posterUrl = widest(video.thumbnailUrls) || video.previewUrl
    return {
      url: video.originalVideoUrl || video.url,
      ...(posterUrl ? { thumbnailUrl: posterUrl, posterUrl } : {}),
    }
  }
  if (media.content.case === 'audioContent') {
    const audio = media.content.value
    const thumbnailUrl = widest(audio.coverThumbnailUrls) || audio.coverImageUrl
    return {
      url: audio.url,
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
    }
  }
  return {}
}

function widest(thumbnails: readonly { url: string; width: number }[]) {
  return [...thumbnails].sort((a, b) => b.width - a.width)[0]?.url
}

function promptRefFrom(ref: string) {
  const match = OUTPUT_TOKEN.exec(ref.trim())
  if (!match?.[1]) {
    return undefined
  }
  return `{{${match[1]}}}`
}
